import React, { FC, useEffect, useState } from 'react';

import { Button, Row } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import * as api from 'services/api';
import apiPaths from 'services/apiPaths';
import { fetchShoppingProducts, fetchStates } from 'src/actions/appActions';
import { updateMagentoData } from 'src/actions/authActions';
import { IResourceList } from 'src/components/challenge/ChallengeInterfaces';
import ChallengeResourceWrapper, {
  ChallengeResourceWrapperBlocked,
} from 'src/components/challenge/ChallengeResourceWrapper';
import CustomModal from 'src/components/CustomModal';
import AddressModal from 'src/components/shoppingCart/AddressModal';
import {
  buildCartInfo,
  getOrderProducts,
  getShoppingCartLabel,
  IOrderProduct,
  modifyProductQuantity,
} from 'src/components/shoppingCart/ShoppingCart.utils';
import ShoppingGroup from 'src/components/shoppingCart/ShoppingCartGroup';
import { IRootReducers } from 'src/reducers';
import {
  IChallengeResourceConfig,
  IChallengeResourceRender,
  IState,
} from 'src/shared/models';
import { handleShoppingCartDirection } from 'utils';

interface IOrderRequest {
  idResource: number;
  products: IOrderProduct[];
  addressId?: number;
}

interface IProps extends IChallengeResourceRender {
  config: IChallengeResourceConfig;
  resource: IResourceList;
  stateList: IState;
}

type IShoppingCart = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  IProps;

export const CustomCloseIcon = () => {
  return (
    <Row className="challengeDetail__modal__close-icon">
      <i className={`icon icon--close`} />
    </Row>
  );
};

const ShoppingCart: FC<IShoppingCart> = ({
  config,
  customer,
  fetchShoppingProducts,
  fetchStates,
  resource,
  shoppingProducts,
  stateList,
  updateChallenge,
  updateMagentoData,
  updateUserPoints,
  userCountry,
}) => {
  const { isCompleted: cartCompleted, isCampaignActivator, icon } = config;
  const [shoppingInfo, setShoppingInfo] = useState(
    buildCartInfo(resource, shoppingProducts)
  );

  const [canCheckout, setCanCheckout] = useState(shoppingInfo.isCorrect);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showConditionsModal, setShowConditionsModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [userAddress, setUserAddress] = useState(null);

  const { formatMessage, locale } = useIntl();

  // TODO
  // Maquetacion Modales
  // Obtener direccion de la api

  // Gestion direcciones
  // Mensajes success / fail -> segun estado
  // Gestion Estados 7-8

  useEffect(() => {
    async function getProducts() {
      setLoading(true);
      await fetchShoppingProducts();
      setLoading(false);
    }

    (async () => await getProducts())();
  }, []);

  useEffect(() => {
    setShoppingInfo(buildCartInfo(resource, shoppingProducts));
  }, [shoppingProducts]);

  useEffect(() => {
    setCanCheckout(shoppingInfo.isCorrect);
  }, [shoppingInfo]);

  const handleChangeQuantity = (idResource: number, quantity: number) => {
    setShoppingInfo(modifyProductQuantity(shoppingInfo, idResource, quantity));
  };

  const handleOrderProducts = async () => {
    setShowOrderModal(true);
    setLoading(true);

    const data: IOrderRequest = {
      idResource: resource.idResource,
      products: getOrderProducts(shoppingInfo),
    };

    try {
      if (handleShoppingCartDirection() && selectedAddress !== null)
        data.addressId = selectedAddress.addressId;

      const response = await api.postDataCall({
        dataPath: apiPaths.CHALLENGES.COMPLETE_SHOPPING_CART,
        data,
        callConfig: {},
      });

      const { score, challenge, address } = response.data;

      if (score && typeof score === 'number') updateUserPoints(score);
      if (address && typeof address === 'string') setUserAddress(address);

      updateChallenge(challenge);
    } catch (err) {
      const stringError: string = err?.response?.data?.message || (
        <FormattedMessage id="register.submit.error" />
      );
      setError(stringError);
    } finally {
      setLoading(false);
    }
  };

  const handleClickOrder = () => {
    if (!canCheckout) return setShowConditionsModal(true);
    if (handleShoppingCartDirection()) return setShowCheckoutModal(true);
    return handleOrderProducts();
  };

  const handleCloseOrder = () => {
    setShowOrderModal(false);
  };

  const button = !cartCompleted && (
    <Row justify="center">
      <Button
        className={`btn-primary btn-primary--black shopping-cart__send-button ${
          !canCheckout ? 'btn-disabled' : ''
        }`}
        onClick={handleClickOrder}
      >
        {shoppingInfo.buttonText}
      </Button>
    </Row>
  );

  useEffect(() => {
    if (!stateList) (async () => fetchStates())();
  }, []);

  const header = (
    <Row
      justify="center"
      className="shopping-cart-header"
      onClick={() => setShowConditionsModal(true)}
    >
      {formatMessage({ id: 'shopping-cart.conditions-label' })}
    </Row>
  );

  const addressModal = (
    <AddressModal
      customer={customer}
      setShowCheckoutModal={setShowCheckoutModal}
      showCheckoutModal={showCheckoutModal}
      stateList={stateList}
      updateMagentoData={updateMagentoData}
      userCountry={userCountry}
    />
  );

  const conditionsModal = (
    <CustomModal
      closeIcon={<CustomCloseIcon />}
      content={
        <>
          <Row
            justify="start"
            align="middle"
            className="conditions-modal__title"
          >
            {formatMessage({ id: 'shopping-cart.conditions-modal.title' })}
          </Row>
          <div
            className="conditions-modal__content block--small regular-body"
            dangerouslySetInnerHTML={{
              __html: shoppingInfo.conditionsContent,
            }}
          />
        </>
      }
      customClass="shopping-cart-modal conditions-modal"
      hideFooter={true}
      onCancel={() => setShowConditionsModal(false)}
      showIcon={false}
      visible={showConditionsModal}
    />
  );

  const orderModalContent = (
    <>
      <Row justify="center" className="block--small title order-modal">
        {formatMessage({ id: 'shopping-cart.label.order-success' })}
      </Row>
      <Row justify="center" className="block--small regular-body">
        {userAddress ?? ''}
      </Row>
    </>
  );

  const orderModal = (
    <CustomModal
      {...{
        content: orderModalContent,
        customClass: 'quiz-modal-finalize',
        error,
        footer: null,
        loading,
        onCancel: handleCloseOrder,
        showIcon: true,
        visible: showOrderModal,
      }}
    />
  );

  const component = (
    <>
      <ChallengeResourceWrapperBlocked />
      {header}
      {shoppingInfo.groups?.map((group, index) => (
        <ShoppingGroup
          config={config}
          group={group}
          handleChangeQuantity={handleChangeQuantity}
          key={index}
        />
      ))}
      {button}

      {addressModal}
      {conditionsModal}
      {orderModal}
    </>
  );

  return (
    <ChallengeResourceWrapper
      bottomLabel={getShoppingCartLabel({
        isCompleted: cartCompleted,
        isActivator: isCampaignActivator,
        isStockCorrect: shoppingInfo.isStockCorrect,
      })}
      children={component}
      description={shoppingInfo.description}
      icon={icon}
      points={formatMessage(
        { id: 'page.challenge.plus-sign-{points}-points' },
        { points: Number(shoppingInfo.points).toLocaleString(locale) }
      )}
      styleClass="challengeDetail--ShoppingCart"
      title={shoppingInfo.name}
    />
  );
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    customer: state.auth.user.magento,
    shoppingProducts: state.app.shoppingProducts,
    stateList: state.app.stateList,
    userCountry: state.auth.user?.salesforce?.userCountry,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    { fetchShoppingProducts, fetchStates, updateMagentoData },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(ShoppingCart);
